import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ComponentRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  inject,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { IonicModule } from "@ionic/angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";

// Models
import { GlobalVarsModel } from "@models/ModelGlobalsVar";

//Store
import { Store } from "@ngrx/store";

// services
import { EventsService } from "@providers/EventsService";
import { NavService } from "@providers/NavService";
import { NewBaseService } from "@providers/newBase.service";
import { DeviceService } from "@services/device.service";
import { SportNavigationService, SportService, SportTrackPollingService } from "@sports-services/index";

//others
import { SPORTS_PATHS } from "@shared-constants/routes";
import { SPORTS_NAVIGATION_SERVICE_ERROR_CODES } from "@sports-services/sports.navigation.service";
import { SPORTS_NAVIGATION_CONFIG } from "@sports-services/sports.navigation.service";
import { Utils } from "@sports-utils/Utils";
import { VIDEOPLAYER_ACTIONS } from "@sports-utils/index";

//components
import { CodereSidebarPcComponent, SbTabsComponent, SportradarWidgetComponent } from "@sports-components/index";
import { MyBetsBetslipComponent } from "@sports-pages/ticket/components/my-bets-betslip/my-bets-betslip";
import { TicketComponent } from "../../ticket/base/ticket/ticket";

//store
import { NewTicketFacade, ticketState } from "@sports-features/ticket/stores";
import { SidebarFacade } from "@sports-stores/sidebar/index";
import { SportsbookFacade, SportsbookState } from "@sports-stores/sportsbook";
import * as sportsbookSelector from "@sports-stores/sportsbook/sportsbook.selectors";

declare global {
  interface Window {
    GLOBALS: any;
  }
}

@Component({
  selector: "codere-page-proxy",
  templateUrl: "./proxy.page.html",
  styleUrls: ["./proxy.page.scss"],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    CodereSidebarPcComponent,
    TicketComponent,
    SportradarWidgetComponent,
    MyBetsBetslipComponent,
    SbTabsComponent,
  ],

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    class: "sportsbook",
  },
})
export class ProxyComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("pageToRender", { read: ViewContainerRef }) pageToRender: ViewContainerRef;
  showTicket = true;

  pageToRenderRef: ComponentRef<any>;
  private ngUnsubscribe$ = new Subject<boolean>();
  sportNavigationService = inject(SportNavigationService);
  private readonly unsubscribe$ = new Subject();
  currentRoute = "";
  pageList = {};
  errorData$: Observable<any>;
  errorDataSub$: Subscription;
  _showError = false;

  /* BETSLIP*/
  // sectionName = '';
  disabledDot$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  ticketState$: Subject<ticketState> = new Subject();
  ticket$: Subject<ticketState> = new Subject();
  keyboard$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  newBaseService = inject(NewBaseService);
  utils = inject(Utils);
  navService = inject(NavService);
  router = inject(Router);
  sportService = inject(SportService);
  events = inject(EventsService);

  route = inject(ActivatedRoute);
  deviceService = inject(DeviceService);
  newTicketFacade = inject(NewTicketFacade);
  sidebarFacade = inject(SidebarFacade);
  sportsbookStore = inject(Store<SportsbookState>);
  _translate = inject(TranslateService);
  sportsbookFacade = inject(SportsbookFacade);
  sportTrackPollingService = inject(SportTrackPollingService);
  globalVars!: GlobalVarsModel;
  isDesktop = false;
  isMobile = false;

  /* BetSlip Filters */
  betslipFilters = [
    {
      name: this._translate.instant("tTitle"),
      disabled: false,
      index: 0,
    },
    {
      name: this._translate.instant("uPHBet"),
      disabled: false,
      index: 1,
    },
  ];
  selectedBetslipFilter = this.betslipFilters[0];

  constructor() {
    this.newBaseService.getVars.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((data) => {
      this.globalVars = data;
      this.checkMyBets();
    });
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();

    this.errorData$ = this.sportsbookStore.select(sportsbookSelector.getSportsErrorData);
    this.errorDataSub$ = this.errorData$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(({ error, currentUrl }) => {
        if (error?.errorQueue?.length && currentUrl && !this._showError) {
          const targetPage = currentUrl.url.split("?")[0].slice(1);
          if (error.errorQueue[0].errorCode === SPORTS_NAVIGATION_SERVICE_ERROR_CODES.CUSTOM_TYPE) {
            this.showError({
              redirectTo: undefined,
              errorText: error.errorQueue[0].errorText,
              reload: undefined,
              back: undefined,
              onlyMessage: true,
            });
          } else if (SPORTS_NAVIGATION_CONFIG[targetPage]?.ERROR_CODE_BEHAVIOR?.[error.errorQueue[0].errorCode]) {
            this.showError(
              SPORTS_NAVIGATION_CONFIG[targetPage].ERROR_CODE_BEHAVIOR[error.errorQueue[0].errorCode],
              error.errorQueue[0]?.errorDescription,
            );
          } else {
            this.sportsbookFacade.consumeSportsError();
          }
        }
      });
  }

  async showError({ redirectTo, errorText, reload, back, onlyMessage }, errorDescription?) {
    this._showError = true;
    if (redirectTo || reload || back || onlyMessage) {
      const alert = await this.utils.sportsAlert(
        true,
        "",
        errorText,
        this._translate.instant("tAccept"),
        () => {},
      );
      alert.onWillDismiss().then(() => {
        this._showError = false;
        if (reload) {
          this.sportsbookFacade.flushSportsErrorQueue();
          this.changePage(this.currentRoute);
        } else if (back) {
          this.sportsbookFacade.flushSportsErrorQueue();
          this.sportNavigationService.back();
        } else if (redirectTo) {
          this.sportsbookFacade.flushSportsErrorQueue();
          this.router.navigate([`/${SPORTS_PATHS[redirectTo]}`], {});
        } else if (onlyMessage) {
          this.sportsbookFacade.consumeSportsError();
        }
      });
    }
  }

  ngOnInit(): void {
    this.initializeApp();
  }

  async initializeApp() {}

  ngAfterViewInit(): void {
    // add sports scope to root
    this.utils.setSportsScope(this);
    this.sportNavigationService.navigationState$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((state) => {
      if (state.currentUrl?.url && !state.currentUrl.popstate && !state.updated) {
        this.changePage(state.currentUrl?.url);
        this.checkMyBets(state.currentUrl?.url);
      }
    });
  }

  changePage(url) {
    this.currentRoute = url;
    const targetPage = url.split("?")[0].slice(1);
    if (this.pageToRender?.element) {
      this.pageToRender.clear();
      this.sportTrackPollingService.stopAllPollings();
    }
    let componentPage: any;
    if (SPORTS_NAVIGATION_CONFIG[targetPage]) componentPage = SPORTS_NAVIGATION_CONFIG[targetPage].PAGE;
    if (componentPage) this.pageToRenderRef = this.pageToRender.createComponent(componentPage);
  }

  public showMoreBets(value: any) {
    let filterParam;
    switch (value) {
      case 0:
        filterParam = { dateValue: 0, typeValue: 0 };
        break;
      case 1:
        filterParam = { dateValue: 0, typeValue: 1 };
        break;
    }
    this.sportNavigationService.navigate([SPORTS_PATHS.MisApuestasPage], {
      state: { params: filterParam },
    });
  }

  public changeTicketType(typeValue): void {
    this.selectedBetslipFilter = this.betslipFilters[typeValue];
    this.changeBetslipTicket(typeValue);
  }

  navigateToTicket() {
    this.changeTicketType(0);
  }

  changeBetslipTicket(typeValue) {
    this.showTicket = typeValue === 0;
  }

  checkMyBets(url?: string) {
    const isMisApuestas = !!url?.includes(SPORTS_PATHS.MisApuestasPage);
    this.betslipFilters = this.betslipFilters.map((betslipFilter) => {
      if (betslipFilter.index !== 0) {
        return {
          ...betslipFilter,
          disabled: !this.globalVars.user.logged || isMisApuestas,
        };
      }
      return betslipFilter;
    });
    if ((!this.globalVars.user.logged || isMisApuestas) && this.selectedBetslipFilter?.index !== 0) {
      this.selectedBetslipFilter = this.betslipFilters[0];
      this.changeBetslipTicket(0);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next(true);
    this.ngUnsubscribe$.complete();
  }

  onWindowResize() {
    this.events.publish(VIDEOPLAYER_ACTIONS.TO_TICKET);
  }
}
